import { motion } from 'framer-motion';
import {
  Truck,
  Bus,
  Wrench,
  AlertTriangle,
  CheckCircle,
  Plus,
  Search,
  Edit,
  Trash2,
  Filter,
  X,
  RefreshCw,
  Users,
  User,
  MapPin,
  MoreHorizontal,
  Download,
} from 'lucide-react';
import { exportToCsv } from '../utils/exportCsv';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdownMenu';
import BusFormModal from '../components/BusFormModal';
import DeleteImpactModal from '../components/DeleteImpactModal';
import { useFeatureFlags } from '../hooks/useFeatureFlags.jsx';
import useBuses from '../hooks/trBuses';
import PageError from '../components/PageError';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useSortPaginate } from '../hooks/useSortPaginate';
import { SortableHeader, TablePagination, ColumnToggle, useColumnVisibility } from '../components/TableControls';
import { StatusBadge } from '../components/ui/StatusBadge';

const BUS_COLUMNS = [
  { key: 'info', label: 'Informations' },
  { key: 'imei', label: 'IMEI' },
  { key: 'assignation', label: 'Assignation' },
  { key: 'status', label: 'Statut' },
];

const BusManagementUnified = () => {
  const { hasFeature } = useFeatureFlags();
  const { visibility, toggle, visible } = useColumnVisibility({
    info: true,
    imei: true,
    assignation: true,
    status: true,
  });
  const {
    filteredBuses,
    loading,
    error,
    stats,
    filters,
    showModal,
    editingBus,
    deleteModalOpen,
    busToDelete,
    updateFilters,
    setShowModal,
    setDeleteModalOpen,
    loadBuses,
    handleAdd,
    handleEdit,
    handleDeleteBus,
    confirmDeleteBus,
    handleSave,
  } = useBuses();

  const { paginated, page, totalPages, totalItems, pageSize, sortCol, sortDir, setPage, toggleSort } = useSortPaginate(
    filteredBuses,
    { col: 'bus_number', dir: 'asc' },
    15,
    [filters],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Bus</h1>
          <p className="text-gray-600">Gérez votre flotte de véhicules</p>
        </div>
        <div className="hidden md:flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const isoToDMY = (d) => (d ? d.replace(/^(\d{4})-(\d{2})-(\d{2}).*/, '$3/$2/$1') : '');
              exportToCsv(
                filteredBuses,
                [
                  { label: 'Numéro', value: (b) => b.bus_number || '' },
                  { label: 'Plaque', value: (b) => b.license_plate || '' },
                  { label: 'Modèle', value: (b) => b.full_model || b.model || '' },
                  { label: 'Année', value: (b) => b.year || '' },
                  { label: 'Capacité', value: (b) => b.capacity || '' },
                  { label: 'IMEI', value: (b) => b.imei || '' },
                  { label: 'Carburant', value: (b) => b.fuel_type || '' },
                  { label: 'Kilométrage', value: (b) => b.odometer_km ?? '' },
                  { label: 'Dernière Maintenance', value: (b) => isoToDMY(b.last_maintenance_date) },
                  { label: 'Statut', value: (b) => b.status || '' },
                  {
                    label: 'Chauffeur',
                    value: (b) =>
                      b.assigned_driver ? `${b.assigned_driver.first_name} ${b.assigned_driver.last_name}` : '',
                  },
                  { label: 'Circuit', value: (b) => b.assigned_route?.route_name || '' },
                ],
                'bus.csv',
              );
            }}
          >
            <Download className="w-4 h-4" />
            Exporter CSV
          </Button>
          <Button onClick={handleAdd}>
            <Plus className="w-4 h-4" />
            Nouveau Bus
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      {!loading && !error && hasFeature('metrics') && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              key: 'total',
              label: 'Total Bus',
              value: stats?.total || 0,
              icon: Truck,
              color: 'blue',
              textColor: 'text-gray-900',
            },
            {
              key: 'active',
              label: 'Actifs',
              value: stats?.active || 0,
              icon: CheckCircle,
              color: 'green',
              textColor: 'text-green-600',
            },
            {
              key: 'maintenance',
              label: 'Maintenance',
              value: stats?.maintenance || 0,
              icon: Wrench,
              color: 'yellow',
              textColor: 'text-yellow-600',
            },
            {
              key: 'inactive',
              label: 'Hors Service',
              value: stats?.inactive || 0,
              icon: AlertTriangle,
              color: 'red',
              textColor: 'text-red-600',
            },
          ].map(({ key, label, value, icon: Icon, color, textColor }, index) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{label}</p>
                  <p className={`text-2xl font-bold ${textColor}`}>{value}</p>
                </div>
                <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 text-${color}-600`} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Barre de filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par numéro, plaque ou modèle"
              value={filters?.search || ''}
              onChange={(e) => updateFilters({ search: e.target.value })}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            {filters?.search && (
              <button
                onClick={() => updateFilters({ search: '' })}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { value: '', label: 'Tous', active: 'bg-blue-100 text-blue-800' },
                { value: 'active', label: 'Actifs', active: 'bg-green-100 text-green-800' },
                { value: 'maintenance', label: 'Maintenance', active: 'bg-yellow-100 text-yellow-800' },
                { value: 'inactive', label: 'Inactif', active: 'bg-gray-100 text-gray-800' },
              ].map(({ value, label, active }) => (
                <button
                  key={label}
                  onClick={() => updateFilters({ status: value })}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filters?.status === value ? active : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={loadBuses}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={14} />
            </button>
            <ColumnToggle columns={BUS_COLUMNS} visibility={visibility} onToggle={toggle} />
          </div>
        </div>
      </div>

      {/* Tableau / Cards — bus */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {!loading && error && <PageError error={error} onRetry={loadBuses} variant="inline" />}
        {!error && (
          <>
            {/* ── Vue cartes (mobile < md) ── */}
            <div className="md:hidden divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={`skel-m-${i}`} className="p-4 flex items-start gap-3">
                    <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-3 w-40" />
                    </div>
                  </div>
                ))
              ) : filteredBuses.length === 0 ? (
                <div className="p-8 text-center">
                  <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun bus trouvé</h3>
                  <p className="text-gray-600 mb-4">
                    {filters?.search || filters?.status
                      ? 'Aucun bus ne correspond aux critères de recherche.'
                      : 'Commencez par ajouter votre premier bus.'}
                  </p>
                  <Button onClick={handleAdd}>
                    <Plus className="w-4 h-4" />
                    Nouveau Bus
                  </Button>
                </div>
              ) : (
                paginated.map((bus, index) => {
                  if (!bus) return null;
                  return (
                    <motion.div
                      key={bus?.id || index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                            <Bus size={20} className="text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900">{bus?.license_plate || 'N/A'}</div>
                            {bus?.ref && <div className="text-xs text-gray-500">{bus.ref}</div>}
                          </div>
                        </div>
                        <StatusBadge status={bus?.status} />
                      </div>
                      <div className="mt-2.5 ml-[52px] space-y-1.5">
                        {(bus?.full_model || bus?.model) && (
                          <div className="flex items-center gap-1.5">
                            <Bus size={13} className="text-gray-400 shrink-0" />
                            <span className="text-sm text-gray-600">
                              {bus?.full_model || bus?.model}
                              {bus?.year ? ` (${bus.year})` : ''}
                              {bus?.capacity ? ` · ${bus.capacity} places` : ''}
                            </span>
                          </div>
                        )}
                        {bus?.imei && (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 font-mono">
                              {bus.imei}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          {bus?.assigned_driver ? (
                            <>
                              <User size={13} className="text-gray-400 shrink-0" />
                              <span className="text-sm text-gray-600">
                                {bus.assigned_driver.first_name} {bus.assigned_driver.last_name}
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle size={13} className="text-orange-400 shrink-0" />
                              <span className="text-sm text-gray-400">Pas de chauffeur</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {bus?.assigned_route ? (
                            <>
                              <MapPin size={13} className="text-gray-400 shrink-0" />
                              <span className="text-sm text-gray-600 truncate max-w-[200px]">
                                {bus.assigned_route.route_name || `Circuit ${bus.assigned_route.route_number}`}
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle size={13} className="text-orange-400 shrink-0" />
                              <span className="text-sm text-gray-400">Pas de circuit</span>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* ── Tableau (desktop ≥ md) ── */}
            {loading ? (
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-[700px] divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Bus', 'Informations', 'IMEI', 'Assignation'].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                      <th className="px-4 py-3 w-[1%] whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-4 py-3 w-[1%] whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <Skeleton className="w-10 h-10 rounded-full" />
                            <div className="space-y-1.5">
                              <Skeleton className="h-3.5 w-20" />
                              <Skeleton className="h-3 w-16" />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1.5">
                            <Skeleton className="h-3.5 w-28" />
                            <Skeleton className="h-3 w-20" />
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Skeleton className="h-5 w-24 rounded-full" />
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1.5">
                            <Skeleton className="h-3.5 w-32" />
                            <Skeleton className="h-3 w-28" />
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end">
                            <Skeleton className="h-7 w-7 rounded" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : filteredBuses.length > 0 ? (
              <>
                <div className="overflow-x-auto hidden md:block">
                  <table className="min-w-[700px] divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortableHeader col="bus_number" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>
                          Bus
                        </SortableHeader>
                        {visible('info') && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Informations
                          </th>
                        )}
                        {visible('imei') && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            IMEI
                          </th>
                        )}
                        {visible('assignation') && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Assignation
                          </th>
                        )}
                        {visible('status') && (
                          <SortableHeader
                            col="status"
                            sortCol={sortCol}
                            sortDir={sortDir}
                            onSort={toggleSort}
                            className="w-[1%] whitespace-nowrap"
                          >
                            Statut
                          </SortableHeader>
                        )}
                        <th className="px-4 py-3 w-[1%] whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginated.map((bus, index) => {
                        if (!bus) return null;
                        return (
                          <tr key={bus?.id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                  <Bus size={20} className="text-blue-600" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{bus?.license_plate || 'N/A'}</div>
                                  {bus?.ref && <div className="text-sm text-gray-500">{bus.ref}</div>}
                                </div>
                              </div>
                            </td>
                            {visible('info') && (
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  <div className="flex items-center mb-1">
                                    <Bus size={14} className="text-gray-400 mr-2" />
                                    {bus?.full_model || bus?.model || 'N/A'} {bus?.year ? `(${bus.year})` : ''}
                                  </div>
                                  <div className="flex items-center">
                                    <Users size={14} className="text-gray-400 mr-2" />
                                    {bus?.capacity || 'N/A'} places
                                  </div>
                                </div>
                              </td>
                            )}
                            {visible('imei') && (
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bus?.imei ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}
                                >
                                  {bus?.imei || 'Non configuré'}
                                </span>
                              </td>
                            )}
                            {visible('assignation') && (
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 flex items-center gap-2">
                                  {bus?.assigned_driver ? (
                                    <>
                                      <User size={14} className="text-gray-400 shrink-0" />
                                      <span>
                                        {bus.assigned_driver.first_name} {bus.assigned_driver.last_name}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <AlertTriangle size={14} className="text-orange-500 shrink-0" />
                                      <span>Pas de chauffeur assigné</span>
                                    </>
                                  )}
                                </div>
                                <div className="text-sm text-gray-900 flex items-center gap-2 mt-1">
                                  {bus?.assigned_route ? (
                                    <>
                                      <MapPin size={14} className="text-gray-400 shrink-0" />
                                      <span>
                                        {bus.assigned_route.route_name || `Circuit ${bus.assigned_route.route_number}`}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <AlertTriangle size={14} className="text-orange-500 shrink-0" />
                                      <span>Pas de circuit assigné</span>
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                            {visible('status') && (
                              <td className="px-4 py-4 whitespace-nowrap">
                                <StatusBadge status={bus?.status} />
                              </td>
                            )}
                            <td className="px-4 py-4 whitespace-nowrap text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    aria-label="Actions"
                                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                  >
                                    <MoreHorizontal size={16} />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-40">
                                  <DropdownMenuItem onClick={() => handleEdit(bus)}>
                                    <Edit size={14} className="mr-2" />
                                    Modifier
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteBus(bus)}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                  >
                                    <Trash2 size={14} className="mr-2" />
                                    Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <TablePagination
                  page={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={setPage}
                />
              </>
            ) : (
              <div className="p-8 text-center hidden md:block">
                <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun bus trouvé</h3>
                <p className="text-gray-600 mb-4">
                  {filters?.search || filters?.status
                    ? 'Aucun bus ne correspond aux critères de recherche.'
                    : 'Commencez par ajouter votre premier bus.'}
                </p>
                <Button onClick={handleAdd}>
                  <Plus className="w-4 h-4" />
                  Nouveau Bus
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <BusFormModal
          isOpen={true}
          bus={editingBus}
          mode={editingBus ? 'edit' : 'create'}
          onSubmit={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}

      {deleteModalOpen && busToDelete && (
        <DeleteImpactModal
          entity={busToDelete}
          entityType="bus"
          entityName={busToDelete.license_plate}
          onConfirm={confirmDeleteBus}
          onCancel={() => setDeleteModalOpen(false)}
        />
      )}
    </div>
  );
};

export default BusManagementUnified;
